/**
 * Secure Data Service
 * Handles loading of sensitive questionnaire and framework data from authenticated APIs
 * instead of loading from static JavaScript files.
 */

import axios from '../utils/axiosConfig';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

class SecureDataService {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    // Check if user has valid session
    const user = localStorage.getItem('user');
    return user !== null;
  }

  /**
   * Generic method to load secure data
   */
  async loadSecureData(endpoint, cacheKey, forceRefresh = false) {
    // Note: Authentication is now handled by the backend endpoints
    // Frontend can load data without explicit auth check

    // Return cached data if available and not forcing refresh
    if (!forceRefresh && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create new loading promise
    const loadingPromise = this.fetchData(endpoint, cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(cacheKey, data);
      return data;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Fetch data from API endpoint
   */
  async fetchData(endpoint, cacheKey) {
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, {
        withCredentials: true,
        params: { lang: i18n.language },
      });

      if (response.data.success || response.data.status === 'SUCCESS') {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to load data');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Handle authentication error
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Authentication expired. Please log in again.');
      }
      throw error;
    }
  }

  /**
   * Load questionnaire data
   */
  async loadQuestionnaireData(forceRefresh = false) {
    return this.loadSecureData(
      'api/secure/questionnaire-data/',
      'questionnaire',
      forceRefresh
    );
  }

  /**
   * Load ISO 27001 framework data
   */
  async loadISOFrameworkData(forceRefresh = false) {
    return this.loadFrameworkData('iso27001', forceRefresh);
  }

  /**
   * Load NIST CSF 1.1 data
   */
  async loadNISTCSF1Data(forceRefresh = false) {
    return this.loadFrameworkData('nist_csf1', forceRefresh);
  }

  /**
   * Generic method to load any framework data dynamically by key
   * Cache key is language-qualified so switching the UI language mid-session
   * fetches the translation overlay instead of serving a stale English (or
   * other-language) response from cache.
   */
  async loadFrameworkData(frameworkKey, forceRefresh = false) {
    const cacheKey = `${frameworkKey}_${i18n.language}`;
    
    if (!forceRefresh && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    const loadingPromise = (async () => {
      try {
        const dataRes = await axios.get(`${API_URL}api/secure/framework-data/${frameworkKey}/`, { 
          withCredentials: true, 
          params: { lang: i18n.language } 
        });
        
        if (dataRes.data.success || dataRes.data.status === 'SUCCESS') {
          return dataRes.data.data;
        }
        throw new Error(dataRes.data.message || 'Failed to load framework data');
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('user');
          window.location.href = '/login';
          throw new Error('Authentication expired. Please log in again.');
        }
        throw error;
      }
    })();

    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(cacheKey, data);
      return data;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Load NIST CSF 2.0 data
   */
  async loadNISTCSF2Data(forceRefresh = false) {
    return this.loadFrameworkData('nist_csf_2_0', forceRefresh);
  }

  /**
   * Load ransomware assessment data
   */
  async loadRansomwareData(forceRefresh = false) {
    return this.loadFrameworkData('ransomware', forceRefresh);
  }

  /**
   * Load RBI CPS data
   */
  async loadRBICPSData(forceRefresh = false) {
    return this.loadFrameworkData('rbi_cps', forceRefresh);
  }

  /**
   * Load CIS V8 data
   */
  async loadCISV8Data(forceRefresh = false) {
    return this.loadFrameworkData('cis_v8_1', forceRefresh);
  }

  /**
   * Load all framework data in a single request
   */
  async loadAllFrameworkData(forceRefresh = false) {
    return this.loadSecureData(
      'api/secure/all-framework-data/',
      'allFrameworks',
      forceRefresh
    );
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(cacheKey) {
    this.cache.delete(cacheKey);
    this.loadingPromises.delete(cacheKey);
  }

  /**
   * Preload all framework data after login
   */
  async preloadAllData() {
    if (!this.isAuthenticated()) {
      return;
    }

    try {
      // Load all data in parallel
      await Promise.all([
        this.loadQuestionnaireData(),
        this.loadISOFrameworkData(),
        this.loadNISTCSF1Data(),
        this.loadNISTCSF2Data(),
        this.loadRansomwareData(),
        this.loadRBICPSData(),
        this.loadCISV8Data()
      ]);
    } catch (error) {
      console.error('Failed to preload framework data:', error);
      // Don't throw error for preloading failures
    }
  }
}

// Create singleton instance
const secureDataService = new SecureDataService();

export default secureDataService;
